<script lang="ts">
  // import SimilarCompany from "$lib/components/blog/similar-company.svelte";
  import BlogIcon from "$lib/components/blog/blog-icons.svelte";
  import Banner from "$lib/components/blog/banner.svelte";
  import GridSkeleton from "$lib/components/ui/skeleton/grid-skeleton.svelte";
  import ProfilePicture from "$lib/components/basic/profilePicture/profilePicture.svelte";
  import type { PageData } from "./$types";
  import { page } from "$app/stores";
  import { ArrowRight } from "lucide-svelte";

  export let data: PageData;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: data.person.name,
    jobTitle: data.person.title,
    description: data.person.shortDescription,
    image: data.person.gravatarUrl,
    sameAs: [`https://www.linkedin.com/in/${data.person.linkedinId}`],
    worksFor: {
      "@type": "Organization",
      name: data.name,
    },
  };

  const serializedJsonLd = `<script type="application/ld+json">${JSON.stringify(jsonLd)}<\/script>`;
</script>

<svelte:head>
  {@html serializedJsonLd}
</svelte:head>

<div
  class="h-[160px] bg-blog-gray-50 bg-cover bg-center dark:bg-blog-gray-950 lg:h-[280px]"
  style="background-image: url('/company-bg.jpg')"
></div>

<div class="mx-auto flex max-w-7xl flex-col-reverse px-4 pt-16 lg:flex-row lg:pb-[6.625rem]">
  <div class="mt-12 lg:mt-0 lg:w-[28%]">
    <Banner link={`/chat-linkedin-profile/${encodeURIComponent(data.linkedin)}`} />
  </div>

  <div class="lg:w-[72%] lg:pl-16">
    <div class="flex items-center gap-4 rounded-md border border-[#DFDFDF] p-4 lg:mb-12 lg:border-0 lg:p-0">
      <div class="h-12 w-12">
        <ProfilePicture imageUrl={data.person.gravatarUrl} altText={data.person.name} shape="circle" outline />
      </div>
      <div class="">
        <h1 class="text-[1.375rem] font-bold leading-[2rem]">{data.person.name}</h1>
        <div class="flex items-center gap-3">
          <span class="text-sm leading-[1.25rem]">
            {data.person.title} @
            <a href={`/companies/${$page.params.slug}`} class="transition-default hover:text-theme-green-deep"
              >{data.name}</a
            >
          </span>
          <a
            href={`https://www.linkedin.com/in/${data.person.linkedinId}`}
            class="text-[12px] leading-[17.6px]"
            target="_blank"
          >
            <BlogIcon name="linkedin" size={16} />
          </a>
        </div>
      </div>
    </div>

    <h2 class="mb-2 mt-6 font-bold">About {data.person.name}</h2>
    <p class="mb-10 text-blog-gray-900 dark:text-blog-gray-300 lg:mb-6">{data.person.shortDescription}</p>

    <div class="mb-10 grid grid-cols-1 gap-7 lg:mb-12 lg:grid-cols-2">
      {#each data.person.profileSections as profile}
        <div class="rounded-md border border-gray-200 p-6 dark:border-gray-800">
          <h3 class="mb-2 font-bold">{profile.title}</h3>
          <p class="text-blog-gray-900 dark:text-blog-gray-300">{profile.content}</p>
        </div>
      {/each}
    </div>

    <h2 class="text-[1.375rem] font-bold tracking-tighter">People similar to {data.person.name}</h2>
    <div class="mt-8 columns-1 gap-7 lg:columns-2">
      {#await data.similarPeople}
        <GridSkeleton count={6} css="h-14 mb-7" />
      {:then similarPeople}
        {#each similarPeople as person}
          <a
            href="/companies/{$page.params.slug}/people/{person.slug}"
            class="group mb-4 flex items-center justify-between gap-2 rounded-md p-3 hover:bg-blog-gray-50 dark:hover:bg-blog-gray-950"
          >
            <div class="flex items-center gap-2">
              <div class="h-14 w-14 min-w-14">
                <ProfilePicture imageUrl={person.gravatarUrl} altText={person.name} shape="circle" outline />
              </div>
              <div>
                <div class="text-blog-gray-1000 dark:text-blog-gray-300">{person.name}</div>
                <div class="font-light text-blog-gray-900 dark:text-blog-gray-300">{person.title}</div>
              </div>
            </div>
            <div class="opacity-0 transition-opacity group-hover:opacity-100">
              <ArrowRight size={20} />
            </div>
          </a>
        {/each}
      {/await}
    </div>
  </div>
</div>
